import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";

const button = cva(
  "inline-flex items-center justify-center gap-2 rounded-(--radius-sm) font-medium " +
    "transition-[background-color,border-color,box-shadow,color] duration-(--dur-fast) ease-(--ease-out) " +
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--brand) " +
    "disabled:opacity-50 disabled:pointer-events-none select-none whitespace-nowrap",
  {
    variants: {
      variant: {
        primary:
          "bg-(--brand) text-(--text-onbrand) shadow-(--shadow-sm) hover:bg-(--brand-strong) active:translate-y-px",
        secondary:
          "bg-(--surface) text-(--text) border border-(--border) shadow-(--shadow-sm) " +
          "hover:bg-(--surface-hover) hover:border-(--border-strong) " +
          "active:translate-y-px active:bg-(--surface-hover) active:shadow-none active:brightness-95",
        ghost:
          "bg-transparent text-(--text-muted) hover:bg-(--surface-hover) hover:text-(--text) " +
          "active:translate-y-px active:bg-(--surface-hover) active:brightness-95",
        danger:
          "bg-(--danger) text-(--text-onbrand) shadow-(--shadow-sm) hover:brightness-110 active:translate-y-px",
      },
      size: {
        // length: hint matters — a bare text-(--var) is treated as text COLOR by both Tailwind and
        // tailwind-merge, so it silently cancels the variant's text-(--text-onbrand) in cn() and the
        // label renders near-black on the brand fill (คุณใหม่ 23/7, prod screenshots).
        sm: "h-8 px-3 text-(length:--text-sm)",
        md: "h-9 px-4 text-(length:--text-sm)",
        lg: "h-11 px-5 text-(length:--text-base)",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof button> {
  /** Shows a spinner and disables the button. */
  loading?: boolean;
}

/** Primary action control. Labels MUST read verb + object (e.g. "Sync data now"), set via children. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, loading, disabled, children, type, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? "button"}
      className={cn(button({ variant, size }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && <Loader2 className="size-4 animate-spin" aria-hidden />}
      {children}
    </button>
  );
});
